import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { NavItem } from './Sidebar';
import { LanguageToggle } from '../ui/LanguageToggle';

interface MobileNavProps {
  mainMenuItems: NavItem[];
  settingsMenuItems: NavItem[];
  currentUser: {
    name: string;
    avatar?: string;
  };
  onLogout?: () => void;
}

export const MobileNav: FC<MobileNavProps> = ({
  mainMenuItems,
  settingsMenuItems,
  currentUser,
  onLogout,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Mobile Header Bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-brand-border h-16 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <img
            src="/images/landing/aidfone-logo-horizontal.png"
            alt="AidFone"
            className="h-8 w-auto"
          />
        </div>

        <div className="flex items-center gap-2">
          <LanguageToggle />
          <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative w-11 h-11 flex items-center justify-center rounded-xl bg-brand-bg-warm hover:bg-brand-primary/10 transition-colors"
          aria-label={isOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          aria-expanded={isOpen}
        >
          <span className={`material-symbols-outlined text-2xl text-brand-text transition-transform duration-300 ${isOpen ? 'rotate-90 opacity-0' : 'rotate-0 opacity-100'}`}>
            menu
          </span>
          <span className={`material-symbols-outlined text-2xl text-brand-text absolute transition-transform duration-300 ${isOpen ? 'rotate-0 opacity-100' : '-rotate-90 opacity-0'}`}>
            close
          </span>
        </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-brand-text/40 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <nav
        className={`md:hidden fixed top-16 right-0 bottom-0 z-50 w-[85vw] max-w-[320px] bg-white shadow-2xl transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* User Profile Card */}
        <div className="p-4 bg-gradient-to-br from-brand-primary to-brand-primary-dark">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center ring-2 ring-white/30">
              {currentUser.avatar ? (
                <img src={currentUser.avatar} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="material-symbols-outlined text-white text-xl">person</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold truncate">{currentUser.name}</p>
              <p className="text-white/70 text-sm">{t('nav.caregiverPortal')}</p>
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="flex-1 overflow-y-auto py-4">
          {/* Main Menu */}
          <div className="px-3 mb-2">
            <p className="px-3 py-2 text-xs font-bold text-brand-text-muted uppercase tracking-wider">
              {t('sidebarUi.navigation')}
            </p>
            <div className="flex flex-col gap-1">
              {mainMenuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-4 rounded-xl font-medium transition-all active:scale-[0.98] ${
                      isActive
                        ? 'bg-brand-primary/10 text-brand-primary'
                        : 'text-brand-text-light hover:bg-brand-bg-warm'
                    }`}
                  >
                    <span
                      className={`material-symbols-outlined text-2xl ${isActive ? '' : 'opacity-60'}`}
                      style={isActive ? { fontVariationSettings: "'FILL' 1" } : {}}
                    >
                      {item.icon}
                    </span>
                    <span className="text-base">{item.label}</span>
                    {isActive && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-brand-primary" />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Settings Menu */}
          <div className="px-3 mt-4">
            <p className="px-3 py-2 text-xs font-bold text-brand-text-muted uppercase tracking-wider">
              {t('nav.settings')}
            </p>
            <div className="flex flex-col gap-1">
              {settingsMenuItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className="flex items-center gap-3 px-3 py-4 rounded-xl text-brand-text-light hover:bg-brand-bg-warm font-medium transition-all active:scale-[0.98]"
                >
                  <span className="material-symbols-outlined opacity-60 text-2xl">
                    {item.icon}
                  </span>
                  <span className="text-base">{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <div className="p-4 border-t border-brand-border">
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand-bg-warm text-brand-text-light font-semibold hover:bg-red-50 hover:text-red-600 transition-colors active:scale-[0.98]"
          >
            <span className="material-symbols-outlined text-xl">logout</span>
            <span>{t('sidebarUi.signOut')}</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default MobileNav;
